import { NetworkStatus } from "@apollo/client";
import {
  ComponentProps,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PanelRightOpenIcon, PlusIcon } from "lucide-react";

import { TableColumnType } from "@/components/@types";
import { useOpenRecord } from "@/app/app/_/routing";
import { BoxForOrganization } from "@/components/atoms/BoxForOrganization";
import { Icon } from "@/components/atoms/Icon";
import { EmptyState, EmptyStateProps } from "@/components/molecules/EmptyState";
import {
  TableStoreProvider,
  useTableStore,
  useTableStoreApi,
} from "@/components/organisms/Table/Table.store";
import { TableDialog } from "@/components/organisms/Table/TableDialog";
import { useEntity } from "@/hooks/useEntity";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { Objects } from "@/objects";
import { iconsByTypename } from "@/objects/@icons";
import type { ObjectType } from "@/objects/@types";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Checkbox } from "@/components/ui/checkbox";

const CARD_MIN_WIDTH = 400;
/**
 * How tall a card is, by its grid's ``size``: ``md`` for a record with a
 * title, a few lines and a picture of itself (a report's miniature), ``sm``
 * for one said in a figure and a line or two (a transaction).
 */
const CARD_HEIGHTS = { sm: 152, md: 266 } as const;
const CARD_GAP = 12;
const OVERSCAN_COUNT = 3; // rows to render before/after visible area

type CardGridProps = {
  /** How tall each card is (``CARD_HEIGHTS``). Defaults to ``md``. */
  size?: keyof typeof CARD_HEIGHTS;
  columns?: TableColumnType[];
  isLoading?: boolean;
  loadNext?: () => void;
  /** May return a promise — the empty state's button spins until it settles. */
  onAddNew?: () => void | Promise<unknown>;
  rows: any[];
  totalCount: number;
  unlockedColumnsStartIndex?: number;
  onCheckedRowsButtonsProps?: ComponentProps<typeof TableDialog>["buttons"];
  onCheckedRowsExtraActions?: ComponentProps<
    typeof TableDialog
  >["extraActions"];
  networkStatus?: NetworkStatus;
  onRowClick?: (row: any) => void;
  /**
   * The record a card is drawn for while its rows load - its kind's
   * ``ObjectType.placeholder`` - so a loading card is the card to come.
   */
  placeholderRow: any;
  /**
   * What the middle of the grid says when the view holds no rows. The button
   * runs ``onAddNew``, so a view that cannot create a record gets the words
   * without one.
   */
  emptyState?: Pick<
    EmptyStateProps,
    "ReactIcon" | "title" | "description" | "actionLabel"
  >;
};

// ---------------------------------------------------------------------------
// ReportsViewCard component – renders a single row's data as a card
// ---------------------------------------------------------------------------

function getTitleText(column: TableColumnType, row: any): string {
  if (!column || !row) return "";
  const value = (column.getValue as (data: any) => unknown)?.(row);
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (value == null) return "";
  if (typeof value === "object") {
    if ("name" in (value as object)) return String((value as any).name);
    if ("label" in (value as object)) return String((value as any).label);
  }
  return String(value);
}

function CardTitle({
  title,
  isLoading,
}: {
  title: string;
  isLoading: boolean;
}) {
  // Cards use a static, non-editable title — editing happens on the record's
  // page or in the panel. Keeping the title static avoids accidental edits
  // while clicking through cards.
  return (
    <Typography level="title-lg" className="truncate flex-1 min-w-0">
      <Skeleton loading={isLoading} variant="inline">
        {title}
      </Skeleton>
    </Typography>
  );
}

/**
 * The business a card's record is in, as the breadcrumb over its title - in
 * the words the triage rows use for theirs, rather than a box beside it.
 */
function CardEntityOverline({
  identifier,
  isLoading,
}: {
  identifier: string;
  isLoading: boolean;
}) {
  const entity = useEntity(identifier);
  return (
    <Typography
      level="body-xs"
      data-testid="data-card-entity"
      className="truncate text-muted-foreground/80 min-w-0"
    >
      <Skeleton
        loading={isLoading || entity.isLoading || !entity.entity}
        variant="inline"
      >
        {entity.entity?.name ?? "Business name"}
      </Skeleton>
    </Typography>
  );
}

function CardCheckbox({ rowId }: { rowId: string }) {
  const isChecked = useTableStore((s) => s.checkedRows[rowId] === true);
  const toggle = useTableStore((s) => s.toggleIsRowChecked);
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex items-center"
      data-testid="data-card-checkbox-wrapper"
      data-checked={isChecked ? "true" : "false"}
    >
      <Checkbox
        checked={isChecked}
        onCheckedChange={() => toggle(rowId)}
        data-testid="data-card-checkbox"
        data-card-id={rowId}
      />
    </div>
  );
}

const Card = memo(function Card({
  row,
  columns,
  isLoading = false,
  onClick,
  onOpenPanel,
}: {
  row: any;
  columns: TableColumnType[];
  isLoading?: boolean;
  onClick?: (row: any) => void;
  /**
   * When ``undefined``, the "Open in panel" button is hidden. CardGrid
   * only passes this through when its caller did *not* supply a custom
   * ``onRowClick`` — i.e. the default whole-card click opens the record's
   * page and the button is the way to the panel instead. When the consumer
   * overrides ``onRowClick`` we drop the button so it doesn't conflict with
   * their intent.
   */
  onOpenPanel?: (row: any) => void;
}) {
  // Per-typename card body — see ``ObjectType.Card`` in
  // ``frontend/src/objects/@types.tsx``. Card view does not fall back to
  // generic column rows; every typename shown in card mode must register
  // ``ObjectType.Card``.
  const typename = (row?.__typename as string | undefined) ?? "";
  const objectType = typename
    ? (Objects as Record<string, ObjectType<any> | undefined>)[typename]
    : undefined;

  // The card is titled with its record's title. The first column is only a
  // stand-in for rows with no object: ledger columns lead with the entity box,
  // whose value is a thing to render, and read as a title it printed the
  // function's source.
  const titleColumn = columns[0];
  const titleText = objectType?.getTitle
    ? (objectType.getTitle(row) ?? "")
    : titleColumn
      ? getTitleText(titleColumn, row)
      : "";
  const CustomCard = objectType?.Card;
  const cardActions = useMemo(
    () => objectType?.getActions?.(row) ?? [],
    [objectType, row],
  );
  const entityId = objectType?.getEntityId?.(row);
  const organization = objectType?.getOrganization?.(row);

  if (!CustomCard && typename) {
    console.error(
      `[CardGrid] Card view requires ObjectType.Card for ${typename}`,
    );
  }

  return (
    <div
      onClick={isLoading ? undefined : () => onClick?.(row)}
      data-testid="data-card"
      data-card-id={isLoading ? undefined : row?.id}
      data-loading={isLoading ? "true" : undefined}
      // The body carries the bottom padding instead (below), so what runs
      // past the card's foot - a report's miniature - is cut by the card's
      // own edge rather than by a strip of padding above it.
      // One standing in for a record is nothing to open yet.
      className={cn(
        "rounded-lg [border-width:1px] [border-style:solid] border-border bg-card p-3 h-full box-border flex flex-col overflow-hidden shadow-xs pb-0 gap-1.5",
        isLoading
          ? ""
          : "cursor-pointer [transition:border-color_0.15s,box-shadow_0.15s] hover:border-input hover:shadow-md",
      )}
    >
      {/* The company it is of or from, by its logo in the card's corner;
          beside it, on the top row, the business it is in, with the
          bulk-select checkbox and the open-in-panel icon button
          right-aligned together - the card itself opens its page; under
          them, the title. A card of a kind that has logos and has none draws
          its kind's icon in a box in its place; a kind without them stands
          flush. Without a business to name, the title is the top row. While
          the list loads, each piece is a skeleton of itself. */}
      {titleColumn && (
        <div className="flex items-center gap-2 min-w-0">
          {objectType?.getOrganization && (
            <Skeleton
              loading={isLoading}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              <BoxForOrganization
                organization={isLoading ? null : organization}
                size="xl"
                fallbackIcon={
                  iconsByTypename[typename as keyof typeof iconsByTypename] ??
                  objectType.ReactIcon
                }
              />
            </Skeleton>
          )}
          <div className="flex flex-col flex-1 min-w-0">
            <div
              data-testid="data-card-top-row"
              className="min-h-0 flex items-center gap-1"
            >
              <div
                data-testid={entityId ? undefined : "data-card-title-row"}
                className="flex flex-1 min-w-0"
              >
                {entityId ? (
                  <CardEntityOverline
                    identifier={idToIdentifier(entityId)}
                    isLoading={isLoading}
                  />
                ) : (
                  <CardTitle title={titleText} isLoading={isLoading} />
                )}
              </div>
              {row?.id && (
                <Skeleton
                  loading={isLoading}
                  variant="rectangular"
                  className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                >
                  <CardCheckbox rowId={row.id} />
                </Skeleton>
              )}
              {onOpenPanel && (
                <Skeleton
                  loading={isLoading}
                  variant="rectangular"
                  className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Open in panel"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenPanel(row);
                        }}
                      >
                        <Icon ReactIcon={PanelRightOpenIcon} size="xs" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Open in panel</TooltipContent>
                  </Tooltip>
                </Skeleton>
              )}
            </div>
            {entityId && (
              <div
                data-testid="data-card-title-row"
                className="mb-0.5 flex min-w-0"
              >
                <CardTitle title={titleText} isLoading={isLoading} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card body – per-typename ``ObjectType.Card`` only. */}
      <div
        // Inside what clips: overflow is cut at the padding's outer edge,
        // which is the card's.
        className="flex flex-col gap-1 flex-1 min-h-0 pb-3 overflow-hidden"
      >
        {CustomCard ? (
          <CustomCard data={row} actions={cardActions} isLoading={isLoading} />
        ) : (
          <div className="flex items-center justify-center flex-1 text-muted-foreground/80">
            <Typography level="body-sm">
              Card renderer not configured
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// CardGrid – virtualized grid of Card components
// ---------------------------------------------------------------------------

export function CardGrid(props: CardGridProps) {
  return (
    <TableStoreProvider>
      <CardGridContent {...props} />
    </TableStoreProvider>
  );
}

function CardGridContent({
  size = "md",
  columns = [],
  isLoading,
  loadNext,
  onAddNew,
  rows,
  totalCount,
  networkStatus,
  onCheckedRowsButtonsProps,
  onCheckedRowsExtraActions,
  onRowClick: providedOnRowClick,
  placeholderRow,
  emptyState,
}: CardGridProps) {
  // Track whether the consumer supplied their own click handler so we can
  // gate the title-row "Open in panel" button: when ``onRowClick`` is
  // omitted the whole-card click opens the record's page and the button is
  // the way to the panel instead. When the consumer overrides
  // ``onRowClick`` we assume they have their own click semantics and drop
  // the shortcut to avoid conflicting intents.
  const isDefaultRowClick = providedOnRowClick === undefined;
  const storeApi = useTableStoreApi();
  const openRecord = useOpenRecord();
  const onRowClick =
    providedOnRowClick ??
    ((row: any) => {
      if (!row?.id) return;
      // A card opens what it is: its record's page, where its kind has one;
      // its dialog otherwise.
      openRecord({ id: row.id, identifier: row.identifier });
    });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    storeApi.setState({
      checkedRows: {},
      isAllRowsChecked: false,
      onRowClick: () => null,
      getRowIdFromRowData: (row) => row?.id ?? row?.identifier ?? "",
      columnDefinitions: columns.map((col, index) => ({
        width: col.width || 200,
        widthMode: col.widthMode,
        originalIndex: index,
        objectKey: col.objectKey,
        label: col.label,
      })),
      layoutIndexToColumnIndex: Array.from(
        { length: columns.length },
        (_, index) => index,
      ),
      unlockedColumnsStartIndex: 1,
    });
  }, [columns, storeApi]);

  useEffect(() => {
    const getRowId = storeApi.getState().getRowIdFromRowData;
    const next = { ...storeApi.getState().checkedRows };
    rows.forEach((row) => {
      const id = getRowId(row);
      if (id && !(id in next)) next[id] = false;
    });
    storeApi.setState({ checkedRows: next });
  }, [rows, storeApi]);

  // Observe container width for responsive columns
  const resizeRef = useResizeObserver((entry) => {
    const width = entry.width;
    if (Math.abs(width - containerWidth) > 10) {
      setContainerWidth(width);
    }
  });

  // Calculate number of columns based on container width
  const numColumns = useMemo(() => {
    if (containerWidth <= 0) return 1;
    return Math.max(
      1,
      Math.floor((containerWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)),
    );
  }, [containerWidth]);

  // Effective total count (show skeleton cards when loading)
  const effectiveTotalCount = isLoading && totalCount === 0 ? 8 : totalCount;

  // Nothing loaded and nothing loading: the grid has no rows to draw, so the
  // middle of it offers the first one instead of an empty scroll area.
  const isEmpty = !isLoading && totalCount === 0 && rows.length === 0;
  // The search term rides in ``filters`` too, so one check covers both.
  const isFiltered = State((state) => state.filters.length > 0);

  // Calculate number of rows in the grid
  const totalRows = Math.ceil(effectiveTotalCount / numColumns);
  const cardHeight = CARD_HEIGHTS[size];
  const rowHeightWithGap = cardHeight + CARD_GAP;
  const totalHeight = totalRows * rowHeightWithGap;

  // Virtualization state
  const [visibleRowCount, setVisibleRowCount] = useState(10);
  const [scrollTop, setScrollTop] = useState(0);

  const startRow = Math.max(
    0,
    Math.floor(scrollTop / rowHeightWithGap) - OVERSCAN_COUNT,
  );
  const endRow = Math.min(
    totalRows,
    Math.floor(scrollTop / rowHeightWithGap) + visibleRowCount + OVERSCAN_COUNT,
  );

  // Trigger loadNext when scrolling near the bottom
  const [shouldTriggerLoadNext, setShouldTriggerLoadNext] = useState(false);

  useEffect(() => {
    if (shouldTriggerLoadNext && loadNext) {
      loadNext();
      setShouldTriggerLoadNext(false);
    }
  }, [shouldTriggerLoadNext, loadNext]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const top = scrollRef.current.scrollTop;
      setScrollTop(top);

      // Update visible row count based on viewport
      const viewportHeight = scrollRef.current.clientHeight;
      const newVisibleCount = Math.ceil(viewportHeight / rowHeightWithGap) + 1;
      if (newVisibleCount !== visibleRowCount) {
        setVisibleRowCount(newVisibleCount);
      }

      // Check if we need to load more
      const endIndex =
        Math.min(
          totalRows,
          Math.floor(top / rowHeightWithGap) + newVisibleCount + OVERSCAN_COUNT,
        ) * numColumns;

      if (
        loadNext &&
        !isLoading &&
        rows.length < totalCount &&
        endIndex >= rows.length - 5
      ) {
        setShouldTriggerLoadNext(true);
      }
    }
  }, [
    totalRows,
    rows.length,
    loadNext,
    isLoading,
    totalCount,
    numColumns,
    rowHeightWithGap,
    visibleRowCount,
  ]);

  return (
    <div
      ref={(el: HTMLDivElement | null) => {
        resizeRef.current = el;
      }}
      className="w-full h-full overflow-hidden relative"
    >
      {isEmpty &&
        (isFiltered ? (
          // Nothing matched, not nothing here: the rows are behind the
          // filters, so this neither says the view is empty nor offers to
          // make its first record.
          <EmptyState
            title="No matches"
            description="Nothing here matches the filters on this view."
          />
        ) : (
          <EmptyState
            ReactIcon={emptyState?.ReactIcon}
            title={emptyState?.title ?? "Nothing here yet"}
            description={emptyState?.description}
            actionLabel={
              onAddNew ? (emptyState?.actionLabel ?? "Create") : undefined
            }
            onAction={onAddNew}
          />
        ))}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={cn(
          "w-full h-full overflow-y-auto overflow-x-hidden p-3",
          isEmpty ? "hidden" : "block",
        )}
      >
        {/* Total height spacer for correct scrollbar */}
        <div
          style={{
            height: totalHeight,
            position: "relative",
          }}
        >
          {/* Rendered rows */}
          {Array.from({ length: Math.max(0, endRow - startRow) }).map(
            (_, rowIdx) => {
              const gridRowIndex = startRow + rowIdx;

              return (
                <div
                  key={`card-row-${gridRowIndex}`}
                  style={{
                    position: "absolute",
                    top: gridRowIndex * rowHeightWithGap,
                    left: 0,
                    right: 0,
                    height: cardHeight,
                    display: "grid",
                    gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                    gap: CARD_GAP,
                  }}
                >
                  {Array.from({ length: numColumns }).map((_, colIdx) => {
                    const itemIndex = gridRowIndex * numColumns + colIdx;
                    if (itemIndex >= effectiveTotalCount) return null;

                    const dataIndex = itemIndex;
                    const isRowLoading =
                      (isLoading && rows.length === 0) ||
                      dataIndex >= rows.length;
                    // A row not here yet is drawn as its kind's typical one.
                    const row = isRowLoading ? placeholderRow : rows[dataIndex];

                    return (
                      <Card
                        key={`card-${dataIndex}`}
                        row={row}
                        columns={columns}
                        isLoading={isRowLoading}
                        onClick={onRowClick}
                        onOpenPanel={
                          isDefaultRowClick
                            ? (r) => {
                                if (!r?.id) return;
                                // Asked for by name, so it opens the panel.
                                State.getState().openNodePanel(r.id, {
                                  autofocus: false,
                                });
                              }
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              );
            },
          )}
        </div>
      </div>

      {onAddNew && (!isEmpty || isFiltered) && (
        <div className="absolute bottom-4 left-4 z-10">
          <Button
            variant="secondary"
            size="sm"
            onClick={onAddNew}
            className="shadow-md pl-4 pr-4"
          >
            <Icon ReactIcon={PlusIcon} size="sm" />
            Add
          </Button>
        </div>
      )}

      <TableDialog
        buttons={onCheckedRowsButtonsProps}
        extraActions={onCheckedRowsExtraActions}
        nameAsSingular="card"
        totalCount={isLoading ? undefined : totalCount}
      />
    </div>
  );
}
